import React, { useEffect, useState } from "react";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Download } from "lucide-react";

// Kept module-scoped so the "beforeinstallprompt" fires once at load, before
// the component tree even mounts (Chrome only sends the event once).
let deferredPrompt = null;
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredPrompt = e;
  });
}

function detectPlatform() {
  const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";
  // In-app browsers (WhatsApp, Instagram, Facebook, TikTok) can't install PWAs
  const inApp = /(FBAN|FBAV|Instagram|Line\/|WhatsApp|TikTok|MicroMessenger|WeChat|MiuiBrowser|GSA\/)/i.test(ua);
  if (inApp) return "inapp";
  if (/iPhone|iPad|iPod/i.test(ua)) return "ios";
  if (/Android/i.test(ua)) return "android";
  return "desktop";
}

const COPY = {
  title: { es: "Instalar Agendik", fr: "Installer Agendik", en: "Install Agendik" },
  android: {
    es: "Si ya instaló Agendik, ábrala desde su pantalla de inicio. Si no, toque el menú ⋮ de Chrome y luego «Instalar aplicación».",
    fr: "Si vous avez déjà installé Agendik, ouvrez-la depuis votre écran d'accueil. Sinon, touchez le menu ⋮ de Chrome puis «Installer l'application».",
    en: "If Agendik is already installed, open it from your home screen. Otherwise, tap the Chrome ⋮ menu and then \"Install app\".",
  },
  ios: {
    es: "Si ya instaló Agendik, ábrala desde su pantalla de inicio. Si no, toque el botón Compartir y luego «Añadir a pantalla de inicio».",
    fr: "Si vous avez déjà installé Agendik, ouvrez-la depuis votre écran d'accueil. Sinon, touchez le bouton Partager puis «Sur l'écran d'accueil».",
    en: "If Agendik is already installed, open it from your home screen. Otherwise, tap the Share button and then \"Add to Home Screen\".",
  },
  inapp: {
    es: "Abra agendik.io en Chrome o Safari para instalar la aplicación.",
    fr: "Ouvrez agendik.io dans Chrome ou Safari pour installer l'application.",
    en: "Open agendik.io in Chrome or Safari to install the app.",
  },
  desktop: {
    es: "Abra agendik.io desde su teléfono para instalar la aplicación.",
    fr: "Ouvrez agendik.io depuis votre téléphone pour installer l'application.",
    en: "Open agendik.io on your phone to install the app.",
  },
};

export default function InstallButton() {
  const { t, lang } = useApp();
  const [open, setOpen] = useState(false);
  const [platform, setPlatform] = useState("desktop");

  useEffect(() => { setPlatform(detectPlatform()); }, []);

  const onClick = async () => {
    if (deferredPrompt) {
      try {
        deferredPrompt.prompt();
        await deferredPrompt.userChoice;
      } finally {
        deferredPrompt = null;
      }
      return;
    }
    setOpen(true);
  };

  const L = (lang === "fr" || lang === "en" || lang === "es") ? lang : "es";
  const message = COPY[platform][L];
  const title = COPY.title[L];

  return (
    <>
      <Button data-testid="install-app-btn" size="sm" variant="outline"
              onClick={onClick}
              className="hidden sm:inline-flex h-9 border-blue-200 text-blue-700 hover:bg-blue-50">
        <Download size={14} className="mr-1.5"/> {t.nav.downloadApp}
      </Button>
      <Button data-testid="install-app-btn-mobile" size="sm" variant="outline"
              onClick={onClick}
              className="sm:hidden h-9 px-2 border-blue-200 text-blue-700">
        <Download size={14}/>
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
          <p data-testid="install-help-message" className="text-sm text-slate-700 leading-relaxed">
            {message}
          </p>
        </DialogContent>
      </Dialog>
    </>
  );
}
